import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Camera, Lightbulb, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ImageSolverModal = ({ open, onOpenChange }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [solving, setSolving] = useState(false);
  const [solution, setSolution] = useState(null);
  const imageInputRef = useRef(null);
  const { getAuthHeaders } = useAuth();

  const handleClose = (isOpen) => {
    if (!isOpen) {
      setSelectedImage(null);
      setImagePreview(null);
      setSolution(null);
    }
    onOpenChange(isOpen);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedImage(file);
    setSolution(null);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSolve = async () => {
    if (!selectedImage || solving) return;
    setSolving(true);

    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const base64 = ev.target.result.split(',')[1];
        try {
          const response = await axios.post(
            `${API_URL}/api/ai/help-solve`,
            { image_base64: base64, lesson_title: '' },
            { headers: getAuthHeaders(), timeout: 120000 }
          );
          setSolution(response.data.solution);
          toast.success('تم تحليل السؤال بنجاح!');
        } catch (error) {
          toast.error('فشل في تحليل السؤال');
          console.error(error);
        } finally {
          setSolving(false);
        }
      };
      reader.readAsDataURL(selectedImage);
    } catch (error) {
      toast.error('فشل في قراءة الصورة');
      setSolving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="solverq max-w-lg" dir="rtl" data-testid="image-solver-modal">
        <DialogHeader>
          <DialogTitle className="ttl">ساعدني في حل سؤال</DialogTitle>
        </DialogHeader>

        {solution ? (
          <div>
            <div className="sol" data-testid="solver-solution-text">{solution}</div>
            <div className="row">
              <button className="pbtn" onClick={() => { setSolution(null); setSelectedImage(null); setImagePreview(null); }} data-testid="solver-new-question-btn">
                سؤال جديد
              </button>
              <button className="gbtn" onClick={() => handleClose(false)}>إغلاق</button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <p className="desc">ارفع صورة السؤال وسيساعدك الذكاء الاصطناعي بخطوات مرتبة لحله بنفسك</p>

            <input
              type="file"
              accept="image/*"
              ref={imageInputRef}
              className="hidden"
              onChange={handleImageSelect}
              data-testid="solver-file-input"
            />

            {imagePreview ? (
              <div className="space-y-3">
                <div className="prev">
                  <img src={imagePreview} alt="السؤال" />
                </div>
                <div className="row" style={{ marginTop: 0 }}>
                  <button className="pbtn" onClick={handleSolve} disabled={solving} data-testid="solver-submit-btn">
                    {solving ? (
                      <><Loader2 className="spin" />جارٍ التحليل...</>
                    ) : (
                      <><Lightbulb />ساعدني في الحل</>
                    )}
                  </button>
                  <button className="gbtn"
                    onClick={() => { setSelectedImage(null); setImagePreview(null); imageInputRef.current?.click(); }}
                    disabled={solving}>
                    تغيير
                  </button>
                </div>
              </div>
            ) : (
              <div className="drop" onClick={() => imageInputRef.current?.click()} data-testid="solver-upload-area">
                <div className="ic"><Camera /></div>
                <b>اضغط لرفع صورة السؤال</b>
                <span>JPG, PNG أو أي صورة</span>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
